// @ts-strict-ignore
import type { FetchResult } from "@apollo/client";
import { useEffect, useState } from "react";

import { SearchFilterTags } from "@shared/components/CatalogSearch/SearchFilterTags/SearchFilterTags";
import { SearchInput } from "@shared/components/CatalogSearch/SearchInput/SearchInput";
import { useSearchHistoriesSaveMutation } from "@shared/data/mutations/SearchHistoriesSaveMutation/SearchHistoriesSave.mutation";
import type { AnalyticsEvent as AnalyticsEventType } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { ViewProductEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import {
  SearchType,
  useAnalyticsLogger,
} from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useHasCatalogSearchShared } from "@shared/hooks/useHasCatalogSearchShared/useHasCatalogSearchShared";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import { useIsFullscriptJsVar } from "@shared/reactiveVar/useIsFullscriptJs/useIsFullscriptJs.store";
import { setProductDetailsEntryPoint } from "@shared/reactiveVar/useProductDetailsLogging/useProductDetailsLogging.store";
import {
  setSearchAnalyticToQuery,
  setSearchAnalyticToTypeahead,
} from "@shared/reactiveVar/useSearchAnalytics/useSearchAnalytics.store";
import type { Typeahead as TypeaheadType } from "@shared/types/graphqlGenerated";

import { FilterButton, FilterButtonStyle } from "../FilterButton/FilterButton";

import * as styles from "./CatalogSearchBarWithFilters.styles";

interface Props {
  onClearFilters: () => void;
  onClearQuery?: () => void;
  showFiltersBeforeSearch?: boolean;
  onFilterAdded?: () => void;
  placeholderOverride?: string;
  entryPointForLogging?: SearchApplyEntryPoint;
  eventNameForSearchLogging?: AnalyticsEventType;
  inputSize?: "normal" | "large";
  isSticky?: boolean;
  showFilterIconIfLessThanWideScreen?: boolean;
  goToProductDetails: (productId: string, productSlug?: string) => void;
  hideFilterTags?: boolean;
  hideFilterButton?: boolean;
  hideSearchField?: boolean;
  isMarginless?: boolean;
  isFocused?: boolean;
  filterStyle?: FilterButtonStyle;
  showRecentSearches?: boolean;
  catalogType?: "DTP" | "WHOLESALE";
  shouldSyncSearchText?: boolean;
  shouldLogOriginUrl?: boolean;
  onOptimizedMegaMenuSearchCallback?: (
    typeahead?: Pick<TypeaheadType, "id" | "name" | "entityType">,
    isQuery?: boolean
  ) => void;
  clearAllPriorSearchParam?: boolean;
}

const CatalogSearchBarWithFilters = ({
  onClearFilters,
  onClearQuery,
  showFiltersBeforeSearch,
  onFilterAdded,
  placeholderOverride,
  entryPointForLogging,
  eventNameForSearchLogging,
  inputSize = "large",
  isSticky,
  showFilterIconIfLessThanWideScreen,
  goToProductDetails,
  hideFilterTags,
  hideFilterButton,
  hideSearchField,
  isMarginless,
  isFocused = true,
  filterStyle = FilterButtonStyle.RESPONSIVE,
  showRecentSearches = false,
  catalogType,
  shouldSyncSearchText = false,
  shouldLogOriginUrl,
  onOptimizedMegaMenuSearchCallback,
  clearAllPriorSearchParam,
  ...rest
}: Props) => {
  const {
    activeIngredients,
    excludedIngredients,
    activeBrands,
    activeCategories,
    activeConditions,
    activeSupplementTypes,
    activeFilters,
    activeTaxonomies,
    searchQuery,
    setSearchQuery,
    isOnlyShowFavorites,
    isOnlyShowInStock,
    isOnlyShowEmersonNew,
    isFsTested,
  } = useCatalogSearch();

  const location = useLocation();
  const hasSearch = useHasCatalogSearchShared();
  const [searchText, setSearchText] = useState<string>(searchQuery);
  const [showSearchContent, setShowSearchContent] = useState(false);
  const isFullscriptJs = useIsFullscriptJsVar();

  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated, isEmerson } = useSharedGlobalConfig();

  const [saveSearchHistory] = useSearchHistoriesSaveMutation();

  useEffect(() => {
    if (shouldSyncSearchText) setSearchText(searchQuery);
  }, [location.pathname]);

  useEffect(() => {
    setSearchText(searchQuery);
  }, [searchQuery]);

  const onClearQuerySearch = () => {
    setSearchText("");
    onClearQuery?.();
  };

  const entrypoint = shouldLogOriginUrl
    ? { origin_url: location.pathname }
    : { entry_point: entryPointForLogging };

  const logSearchApplyEvent = (recentSearchQuery?: string, recentSearchRank?: number) => {
    if (!entryPointForLogging || !eventNameForSearchLogging) return;
    if (isUnauthenticated) return;

    setSearchAnalyticToQuery(
      eventNameForSearchLogging,
      entryPointForLogging,
      recentSearchQuery ?? searchText,
      recentSearchRank
    );

    if (!isEmerson) return;

    logAnalyticsEvent({
      eventName: eventNameForSearchLogging,
      payload: {
        ...entrypoint,
        content: recentSearchQuery ?? searchText,
        type: SearchType.QUERY,
        search_version: 2,
        ...(recentSearchRank && { recent_search_rank: recentSearchRank }),
      },
    });
  };

  const logTypeaheadSearchApplyEvent = (
    entityName: string,
    entityType: SearchType,
    recentSearchRank?: number
  ) => {
    if (!entryPointForLogging || !eventNameForSearchLogging) return;
    if (isUnauthenticated) return;

    setSearchAnalyticToTypeahead(
      eventNameForSearchLogging,
      entryPointForLogging,
      entityName,
      entityType,
      recentSearchRank
    );

    if (!isEmerson) return;

    logAnalyticsEvent({
      eventName: eventNameForSearchLogging,
      payload: {
        ...entrypoint,
        content: entityName,
        type: entityType,
        search_version: 2,
        ...(recentSearchRank && { recent_search_rank: recentSearchRank }),
      },
    });
  };

  const handleSearchContentOpen = () => {
    setShowSearchContent(true);
  };

  const hasMoreThanOneActiveFilter = (): boolean => {
    let totalActiveFilters = 0;

    if (isOnlyShowFavorites) {
      totalActiveFilters += 1;
    }

    if (isOnlyShowInStock) {
      totalActiveFilters += 1;
    }

    if (isOnlyShowEmersonNew) {
      totalActiveFilters += 1;
    }

    if (isFsTested) {
      totalActiveFilters += 1;
    }

    totalActiveFilters +=
      (activeBrands?.length ?? 0) +
      (activeFilters?.length ?? 0) +
      (activeCategories?.length ?? 0) +
      (activeIngredients?.length ?? 0) +
      (excludedIngredients?.length ?? 0) +
      (activeConditions?.length ?? 0) +
      (activeTaxonomies?.length ?? 0) +
      (activeSupplementTypes?.length ?? 0);

    return totalActiveFilters > 1;
  };

  const onTypeaheadSelection = (query?: string) => {
    setSearchText(query ?? searchQuery);
    setShowSearchContent(false);
  };

  const onQuerySearch = () => {
    setSearchAnalyticToQuery(eventNameForSearchLogging, entryPointForLogging, searchText);
    logSearchApplyEvent();
    setShowSearchContent(false);
    setSearchQuery(searchText, undefined, clearAllPriorSearchParam);
    onFilterAdded?.();
    onOptimizedMegaMenuSearchCallback?.(null, true);
  };

  const clearFilters = () => {
    onClearFilters();
  };

  const onSearchValueChange = (value: string) => {
    setSearchText(value);
    setShowSearchContent(true);
  };

  const isSearchExpanded = showSearchContent && searchText;

  const renderFiltersButton = () => {
    if (hideFilterButton) {
      return null;
    }
    const filterButton = (
      <FilterButton
        entryPointForLogging={entryPointForLogging}
        showFilterIconIfLessThanWideScreen={showFilterIconIfLessThanWideScreen}
        setShowSearchContent={setShowSearchContent}
        isSearchExpanded={!!isSearchExpanded}
        inputSize={inputSize}
        type={filterStyle}
      />
    );

    if (showFiltersBeforeSearch) {
      return filterButton;
    }

    if (hasSearch) {
      return filterButton;
    }

    return null;
  };

  const renderSearchFilterTags = () => {
    if (hideFilterTags) {
      return null;
    }

    return <SearchFilterTags onClearFilters={hasMoreThanOneActiveFilter() && clearFilters} />;
  };

  const saveProductTypeaheadClickToSearchHistory = (
    productId: string
  ): Promise<FetchResult<null>> => {
    return saveSearchHistory({
      variables: {
        input: {
          filterId: productId,
          ...(catalogType && { source: catalogType }),
        },
      },
    });
  };

  const handleProductClick = async (productId: string, productSlug?: string) => {
    setShowSearchContent(false);

    const entryPoint = !isFullscriptJs
      ? ViewProductEntryPoint.CARE_CENTER_TYPE_AHEAD
      : ViewProductEntryPoint.CATALOG_TYPE_AHEAD;

    setProductDetailsEntryPoint(entryPoint);

    if (showRecentSearches) {
      try {
        await saveProductTypeaheadClickToSearchHistory(productId);
      } finally {
        goToProductDetails(productId, productSlug);
      }
    } else {
      goToProductDetails(productId, productSlug);
    }
  };

  const renderSearchBarWithFilterButton = () => {
    if (hideSearchField) {
      return null;
    }

    return (
      <div
        css={[
          styles.searchWrapper,
          isSticky && styles.searchWrapperSticky,
          isMarginless && styles.marginless,
        ]}
        {...rest}
      >
        <SearchInput
          onSearchContentOpen={handleSearchContentOpen}
          onSearchContentClose={() => setShowSearchContent(false)}
          onQuerySearch={onQuerySearch}
          onClearQuerySearch={onClearQuerySearch}
          onTypeaheadSelection={onTypeaheadSelection}
          onSearchValueChange={onSearchValueChange}
          searchValue={searchText}
          showSearchContent={showSearchContent}
          placeholderOverride={placeholderOverride}
          goToProductDetails={handleProductClick}
          logTypeaheadAnalytics={logTypeaheadSearchApplyEvent}
          logSearchApplyEvent={logSearchApplyEvent}
          inputSize={inputSize}
          isFocused={isFocused}
          showRecentSearches={showRecentSearches}
          catalogType={catalogType}
          onOptimizedMegaMenuSearchCallback={onOptimizedMegaMenuSearchCallback}
          clearAllPriorSearchParam={clearAllPriorSearchParam}
          inputType="search"
          enterKeyHint="search"
        />

        {renderFiltersButton()}
      </div>
    );
  };

  return (
    <div css={styles.container}>
      {renderSearchBarWithFilterButton()}
      {renderSearchFilterTags()}
    </div>
  );
};

export { CatalogSearchBarWithFilters };
